import React from 'react';
import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from 'sonner';

import { AuthProvider } from './context/AuthContext';
import { I18nProvider } from './context/I18nContext';
import { NotificationsProvider } from './context/NotificationsContext';
import ProtectedRoute from './components/ProtectedRoute';

import AuthPage from './pages/AuthPage';
import AuthCallback from './pages/AuthCallback';
import FeedPage from './pages/FeedPage';
import ProfilePage from './pages/ProfilePage';
import EditProfilePage from './pages/EditProfilePage';
import AdminPage from './pages/AdminPage';
import BattlesPage from './pages/BattlesPage';
import DiscoverPage from './pages/DiscoverPage';
import CreatePostPage from './pages/CreatePostPage';
import PostPage from './pages/PostPage';
import HashtagPage from './pages/HashtagPage';
import NotificationsPage from './pages/NotificationsPage';
import SearchPage from './pages/SearchPage';
import QRPage from './pages/QRPage';
import ScannerPage from './pages/ScannerPage';
import BattleDetailPage from './pages/BattleDetailPage';
import RecognizePage from './pages/RecognizePage';
import CommunityAuraPage from './pages/CommunityAuraPage';
import BattleStylePage from './pages/BattleStylePage';
import ContestsDirectoryPage from './pages/ContestsDirectoryPage';
import EventCalendarPage from './pages/EventCalendarPage';
import OrganizerDashboardPage from './pages/OrganizerDashboardPage';
import EventDetailPage from './pages/EventDetailPage';
import EventVotePage from './pages/EventVotePage';
import LandingPage from './pages/LandingPage';
import PlansPage from './pages/PlansPage';
import PrizesPage from './pages/PrizesPage';
import { PaymentSuccessPage, PaymentCancelPage } from './pages/PaymentPages';
import { TermsPage, PrivacyPage } from './pages/LegalPages';
import SuggestionsPage from './pages/SuggestionsPage';
import AdminSuggestionsPage from './pages/AdminSuggestionsPage';

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/home" element={<ProtectedRoute><FeedPage /></ProtectedRoute>} />
      <Route path="/me" element={<ProtectedRoute><ProfilePage mode="me" /></ProtectedRoute>} />
      <Route path="/me/edit" element={<ProtectedRoute><EditProfilePage /></ProtectedRoute>} />
      <Route path="/u/:username" element={<ProfilePage mode="public" />} />
      <Route path="/battles" element={<ProtectedRoute><BattlesPage /></ProtectedRoute>} />
      <Route path="/discover" element={<ProtectedRoute><DiscoverPage /></ProtectedRoute>} />
      <Route path="/search" element={<ProtectedRoute><SearchPage /></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />

      <Route path="/create/text" element={<ProtectedRoute><CreatePostPage variant="text" /></ProtectedRoute>} />
      <Route path="/create/photo" element={<ProtectedRoute><CreatePostPage variant="photo" /></ProtectedRoute>} />
      <Route path="/create/video" element={<Navigate to="/create/photo" replace />} />
      <Route path="/create/aura-moment" element={<ProtectedRoute><CreatePostPage variant="aura_moment" /></ProtectedRoute>} />
      <Route path="/create/positive-action" element={<ProtectedRoute><CreatePostPage variant="positive" /></ProtectedRoute>} />

      <Route path="/post/:publicId" element={<PostPage />} />
      <Route path="/tag/:tag" element={<HashtagPage />} />

      <Route path="/me/qr" element={<ProtectedRoute><QRPage /></ProtectedRoute>} />
      <Route path="/scan" element={<ProtectedRoute><ScannerPage /></ProtectedRoute>} />
      <Route path="/battle/:id" element={<ProtectedRoute><BattleDetailPage /></ProtectedRoute>} />
      <Route path="/recognize/:token" element={<ProtectedRoute><RecognizePage /></ProtectedRoute>} />
      <Route path="/community/mine" element={<ProtectedRoute><CommunityAuraPage /></ProtectedRoute>} />
      <Route path="/me/battle-style" element={<ProtectedRoute><BattleStylePage /></ProtectedRoute>} />
      <Route path="/contests" element={<ContestsDirectoryPage />} />
      <Route path="/events" element={<EventCalendarPage />} />
      <Route path="/events/organizer" element={<ProtectedRoute><OrganizerDashboardPage /></ProtectedRoute>} />
      <Route path="/events/:eventId" element={<EventDetailPage />} />
      <Route path="/vote" element={<ProtectedRoute><EventVotePage /></ProtectedRoute>} />
      <Route path="/plans" element={<PlansPage />} />
      <Route path="/prizes" element={<ProtectedRoute><PrizesPage /></ProtectedRoute>} />
      <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccessPage /></ProtectedRoute>} />
      <Route path="/payment/cancel" element={<PaymentCancelPage />} />
      <Route path="/terminos" element={<TermsPage />} />
      <Route path="/privacidad" element={<PrivacyPage />} />

      <Route path="/admin" element={<ProtectedRoute adminOnly><AdminPage /></ProtectedRoute>} />
      <Route path="/admin/suggestions" element={<ProtectedRoute adminOnly><AdminSuggestionsPage /></ProtectedRoute>} />
      <Route path="/suggestions" element={<ProtectedRoute><SuggestionsPage /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/home" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App font-body">
      <I18nProvider>
        <AuthProvider>
          <NotificationsProvider>
            <BrowserRouter>
              <AppRouter />
              <Toaster position="top-center" theme="dark" toastOptions={{ style: { background: '#0F0F16', border: '1px solid rgba(0,240,255,0.3)', color: '#fff' } }} />
            </BrowserRouter>
          </NotificationsProvider>
        </AuthProvider>
      </I18nProvider>
    </div>
  );
}
